import { create } from "zustand";
import { persist } from "zustand/middleware";

import { fetchMe, login as apiLogin, logoutCall, refresh as apiRefresh } from "@/api/auth";
import type { Me, TokenPair } from "@/api/auth";

type AuthState = {
  accessToken: string | null;
  refreshToken: string | null;
  expiresAt: number | null; // epoch ms
  me: Me | null;
  status: "anonymous" | "authenticating" | "authenticated" | "error";
  error: string | null;

  // Actions
  signIn: (email: string, password: string, mfaCode?: string) => Promise<void>;
  /** Adopt a pair obtained outside the password flow (SSO callback). */
  signInWithTokens: (tp: TokenPair) => Promise<void>;
  signOut: () => Promise<void>;
  loadMe: () => Promise<void>;
  applyTokens: (tp: TokenPair) => void;
  refreshAccess: () => Promise<string | null>;
  isAuthenticated: () => boolean;
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      expiresAt: null,
      me: null,
      status: "anonymous",
      error: null,

      applyTokens: (tp) => {
        const expiresAt = Date.now() + tp.expires_in * 1000;
        set({
          accessToken: tp.access_token,
          refreshToken: tp.refresh_token,
          expiresAt,
        });
      },

      signIn: async (email, password, mfaCode) => {
        set({ status: "authenticating", error: null });
        try {
          const tp = await apiLogin(email, password, mfaCode);
          get().applyTokens(tp);
          const me = await fetchMe();
          set({ me, status: "authenticated", error: null });
        } catch (e) {
          const msg =
            (e as { response?: { data?: { detail?: unknown } } }).response?.data?.detail ??
            (e as Error).message ??
            "login_failed";
          set({
            status: "error",
            error: typeof msg === "string" ? msg : JSON.stringify(msg),
            accessToken: null,
            refreshToken: null,
            expiresAt: null,
            me: null,
          });
          throw e;
        }
      },

      signInWithTokens: async (tp) => {
        set({ status: "authenticating", error: null });
        try {
          get().applyTokens(tp);
          const me = await fetchMe();
          set({ me, status: "authenticated", error: null });
        } catch (e) {
          set({
            status: "error",
            error: (e as Error).message ?? "sso_login_failed",
            accessToken: null,
            refreshToken: null,
            expiresAt: null,
            me: null,
          });
          throw e;
        }
      },

      signOut: async () => {
        try {
          if (get().accessToken) {
            await logoutCall();
          }
        } catch {
          /* swallow */
        }
        set({
          accessToken: null,
          refreshToken: null,
          expiresAt: null,
          me: null,
          status: "anonymous",
          error: null,
        });
      },

      loadMe: async () => {
        if (!get().accessToken) return;
        try {
          const me = await fetchMe();
          set({ me, status: "authenticated" });
        } catch {
          set({
            accessToken: null,
            refreshToken: null,
            expiresAt: null,
            me: null,
            status: "anonymous",
          });
        }
      },

      refreshAccess: async () => {
        const { refreshToken } = get();
        if (!refreshToken) return null;
        try {
          const tp = await apiRefresh(refreshToken);
          get().applyTokens(tp);
          return tp.access_token;
        } catch {
          set({
            accessToken: null,
            refreshToken: null,
            expiresAt: null,
            me: null,
            status: "anonymous",
          });
          return null;
        }
      },

      isAuthenticated: () => Boolean(get().accessToken),
    }),
    {
      name: "cortex-auth",
      partialize: (s) => ({
        accessToken: s.accessToken,
        refreshToken: s.refreshToken,
        expiresAt: s.expiresAt,
      }),
    },
  ),
);
